function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useMemo, createElement as _createElement } from 'react';
import GenericError from "../../../components/GenericError/GenericError";
import RestfulWrapper from "../../../components/RestfulWrapper/RestfulWrapper";
import GraphWidgetLayout from "../GraphWidget/GraphWidgetLayout";
import GraphWidgetTopCard from "../GraphWidget/GraphWidgetTopCard";
import { Spinner, Typography, WithInfoIcon } from 'seagull';
import GraphWidgetBottomCard from "../GraphWidget/GraphWidgetBottomCard";
import styles from "./SalesWidget.module.scss";
import { useIoContext, useStore, useStoreActions } from "../../../contexts";
import { selectors } from "../../../store/state-selectors";
import { SalesSummaryStatus } from "../../../models";
import ConditionalWrapper from "../../../components/ConditionalWrapper/ConditionalWrapper";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { commonBarProps, commonCartesianGridProps, commonXAxisProps } from "../charts/commonChartProps";
import CustomLegend from "../charts/CustomLegend/CustomLegend";
import colors from 'seagull/foundations/colors.module.scss';
import CustomXAxisTick from "../charts/CustomXAxisTick/CustomXAxisTick";
import { format } from 'date-fns';
import { utils } from "../../../utils";
import CustomTooltip from "../charts/CustomTooltip/CustomTooltip";
import CustomBarShape from "../charts/CustomBarShape/CustomBarShape";
import { toDatestampString } from "../../../models/time";
import Decimal from 'decimal.js';
import { getXAxisTextForQuarterlyDate } from "../charts/charts.utils";
import { getLastXMonths } from "../../../utils/date.utils";
import pluralize from "../../../utils/pluralize";
import { routes } from "../../../constants";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const statusesOrder = [SalesSummaryStatus.Approved, SalesSummaryStatus.Reviewed, SalesSummaryStatus.Sent, SalesSummaryStatus.Expired, SalesSummaryStatus.Canceled, SalesSummaryStatus.Terminated];
const statusToText = {
  [SalesSummaryStatus.Approved]: 'Signed',
  [SalesSummaryStatus.Reviewed]: 'Reviewed',
  [SalesSummaryStatus.Sent]: 'Not reviewed',
  [SalesSummaryStatus.Canceled]: 'Withdrawn',
  [SalesSummaryStatus.Terminated]: 'Terminated',
  [SalesSummaryStatus.Expired]: 'Expired'
};
const statusToColor = {
  [SalesSummaryStatus.Approved]: colors.blue700,
  [SalesSummaryStatus.Reviewed]: colors.purple700,
  [SalesSummaryStatus.Sent]: colors.purple300,
  [SalesSummaryStatus.Canceled]: colors.dark200,
  [SalesSummaryStatus.Terminated]: colors.red500,
  [SalesSummaryStatus.Expired]: colors.purple50
};
const renderBar = status => /*#__PURE__*/_createElement(Bar, _objectSpread(_objectSpread({}, commonBarProps), {}, {
  key: status,
  dataKey: item => item.statuses[status].total.toNumber(),
  stackId: "a",
  fill: statusToColor[status],
  shape: /*#__PURE__*/_jsx(CustomBarShape, {
    getHasRadius: item => {
      const statusIndex = statusesOrder.indexOf(status);
      const statusesAbove = statusesOrder.slice(statusIndex + 1);
      return !statusesAbove.some(s => item.statuses[s].total.gt(0));
    },
    getTestId: item => "".concat(status, "-for-").concat(toDatestampString(item.date))
  })
}));
export const renderTooltip = function renderTooltip(testid) {
  let additionalProps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return /*#__PURE__*/_jsx(CustomTooltip, _objectSpread(_objectSpread({}, additionalProps), {}, {
    skin: "dark",
    testid: "".concat(testid, "-tooltip"),
    getTitle: (label, payload) => {
      if (!payload || !payload.length) return '';
      const item = payload[0].payload;
      return format(item.date, 'MMMM');
    },
    getItems: payload => {
      if (!payload || !payload.length) return;
      const item = payload[0].payload;
      return statusesOrder.filter(s => item.statuses[s].total.gt(0)).map(s => ({
        id: s,
        label: "".concat(utils.formatNumberWithCommas(item.statuses[s].total), " ").concat(statusToText[s]),
        color: statusToColor[s]
      }));
    },
    getBody: payload => {
      if (!payload || !payload.length) return null;
      const item = payload[0].payload;
      const totalProposals = Object.values(item.statuses).reduce((acc, val) => acc.add(val.total), new Decimal(0)).toNumber();
      return "Out of ".concat(pluralize('proposal', totalProposals, true), " sent:");
    }
  }));
};
export function getLegendItems(data) {
  return statusesOrder.filter(status => data.some(item => item.statuses[status].total.gt(0))).map(status => ({
    status,
    label: statusToText[status],
    color: statusToColor[status]
  }));
}
function SalesWidgetChart(_ref) {
  let {
    testid,
    data,
    isTestMode
  } = _ref;
  const barChartProps = isTestMode ? {
    height: 100,
    width: 100
  } : {};
  const legendItems = useMemo(() => getLegendItems(data), [data]);
  return /*#__PURE__*/_jsx("div", {
    "data-testid": testid,
    className: styles.chart,
    children: /*#__PURE__*/_jsx(ConditionalWrapper, {
      condition: !isTestMode,
      wrapper: children => /*#__PURE__*/_jsx(ResponsiveContainer, {
        children: children
      }),
      children: /*#__PURE__*/_jsxs(BarChart, _objectSpread(_objectSpread({}, barChartProps), {}, {
        data: data,
        barCategoryGap: "10%",
        margin: {
          top: 20,
          left: 16,
          right: 16
        },
        children: [/*#__PURE__*/_jsx(CartesianGrid, _objectSpread({}, commonCartesianGridProps)), /*#__PURE__*/_jsx(Legend, {
          content: /*#__PURE__*/_jsx(CustomLegend, {
            testid: "".concat(testid, "-legend"),
            items: legendItems,
            gap: 16
          })
        }), /*#__PURE__*/_jsx(XAxis, _objectSpread(_objectSpread({}, commonXAxisProps), {}, {
          dataKey: item => item.date,
          height: 49,
          tick: /*#__PURE__*/_jsx(CustomXAxisTick, {
            tickHeight: 30,
            getText: getXAxisTextForQuarterlyDate
          })
        })), /*#__PURE__*/_jsx(YAxis, {
          axisLine: false,
          tickLine: false,
          width: 32,
          tickFormatter: value => value === 0 ? '' : utils.formatNumberWithCommas(value),
          fontSize: 12
        }), /*#__PURE__*/_jsx(Tooltip, {
          cursor: false,
          isAnimationActive: false,
          content: renderTooltip(testid)
        }), statusesOrder.map(renderBar)]
      }))
    })
  });
}
const TopCard = _ref2 => {
  let {
    label,
    info,
    value,
    loading,
    testid,
    linkTo
  } = _ref2;
  return /*#__PURE__*/_jsx(GraphWidgetTopCard, {
    "data-testid": testid,
    linkTo: linkTo,
    children: /*#__PURE__*/_jsxs("div", {
      className: styles.topCard,
      children: [/*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(WithInfoIcon, {
          info: info,
          children: /*#__PURE__*/_jsx(Typography, {
            children: label
          })
        })
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "h5",
        weight: "normal",
        "data-testid": "".concat(testid, "-value"),
        children: loading ? /*#__PURE__*/_jsx(Spinner, {
          size: 30,
          "data-testid": "".concat(testid, "-loading")
        }) : value
      })]
    })
  });
};
function getMockChartData(now) {
  return getLastXMonths(now, 12).map(date => ({
    date,
    statuses: {
      [SalesSummaryStatus.Sent]: {
        total: new Decimal(2)
      },
      [SalesSummaryStatus.Approved]: {
        total: new Decimal(3)
      },
      [SalesSummaryStatus.Terminated]: {
        total: new Decimal(0)
      },
      [SalesSummaryStatus.Canceled]: {
        total: new Decimal(1)
      },
      [SalesSummaryStatus.Reviewed]: {
        total: new Decimal(1)
      },
      [SalesSummaryStatus.Expired]: {
        total: new Decimal(0)
      }
    }
  }));
}
function MockSalesWidgetChart(_ref3) {
  let {
    isTestMode
  } = _ref3;
  const {
    getNow
  } = useIoContext();
  const data = useMemo(() => getMockChartData(getNow()), []);
  return /*#__PURE__*/_jsx(SalesWidgetChart, {
    data: data,
    testid: "sales-widget-mock-chart",
    isTestMode: isTestMode
  });
}
function SalesWidgetContent(_ref4) {
  let {
    loading,
    data,
    isTestMode
  } = _ref4;
  const hasSentAProposal = useStore(selectors.hasSentAProposal);
  const areCardsLoading = !data;
  const {
    PROPOSALS_REPORT
  } = useFeatureFlags();
  return /*#__PURE__*/_jsxs(GraphWidgetLayout, {
    title: "Sent proposals",
    loading: loading,
    bottomHeight: "l",
    testid: "sales-widget",
    children: [/*#__PURE__*/_jsx(TopCard, {
      loading: areCardsLoading,
      value: data ? data.formattedNumProposalsSentThisYear() : '',
      testid: "sales-widget-proposals-sent-this-year",
      label: "Proposals sent this year",
      info: "The total number of proposals you've sent through Anchor so far this year (Since Jan 1).",
      linkTo: PROPOSALS_REPORT ? routes.proposalsReport : undefined
    }), /*#__PURE__*/_jsx(TopCard, {
      loading: areCardsLoading,
      value: data ? data.formattedMedianTimeToSign() : '',
      testid: "sales-widget-median-time-to-sign",
      label: "Median time to sign",
      info: "The median time it takes for clients to sign proposals within the past 90 days."
    }), /*#__PURE__*/_jsx(GraphWidgetBottomCard, {
      "data-testid": "monthly-sales".concat(areCardsLoading ? '-loading' : ''),
      loading: areCardsLoading,
      loadingMsg: "Loading data...",
      empty: data && data.isEmpty() && !hasSentAProposal,
      emptyMsg: "Here you will view your sales over time",
      loadingOrEmptyBg: () => /*#__PURE__*/_jsx(MockSalesWidgetChart, {
        isTestMode: isTestMode
      }),
      children: data && /*#__PURE__*/_jsx(SalesWidgetChart, {
        data: data.calendar(),
        testid: "sales-widget-chart",
        isTestMode: isTestMode
      })
    })]
  });
}
export default function SalesWidget(_ref5) {
  let {
    isTestMode
  } = _ref5;
  const initialData = useStore(state => state.salesSummary.data);
  const {
    fetchSalesSummary
  } = useStoreActions();
  return /*#__PURE__*/_jsx(RestfulWrapper, {
    initialData: initialData,
    fetch: fetchSalesSummary,
    renderChild: _ref6 => {
      let {
        data,
        loading
      } = _ref6;
      return /*#__PURE__*/_jsx(SalesWidgetContent, {
        loading: loading,
        data: data,
        isTestMode: isTestMode
      });
    },
    renderLoader: () => /*#__PURE__*/_jsx(SalesWidgetContent, {
      loading: true,
      isTestMode: isTestMode
    }),
    renderError: () => /*#__PURE__*/_jsx(GenericError, {
      testid: "widget-error"
    })
  });
}